/**
 * useAdditionalFeeRequests — query the additional_fee_requests table.
 *
 * Scope is enforced by RLS. The query returns rows ordered by
 * created_at DESC so the newest pending requests sit at the top of
 * the owner-approver queue. Caller can scope by status filter or by
 * cleaning_task_id (for the cleaner's task-detail surface).
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'

export type AdditionalFeeStatus =
  | 'requested'
  | 'approved'
  | 'denied'
  | 'completed_without_approval'
  | 'paid_at_normal_clean_rate'

export interface AdditionalFeeRequestRow {
  id:                            string
  organization_id:               string
  cleaning_task_id:              string
  room_id:                       string
  requested_by_user_id:          string
  status:                        AdditionalFeeStatus
  requested_amount_cents:        number
  reason:                        string
  photo_storage_paths:           string[]
  owner_decision_by_user_id:     string | null
  owner_decision_at:             string | null
  denial_reason:                 string | null
  created_at:                    string
  updated_at:                    string
}

export interface UseAdditionalFeeRequestsArgs {
  /** When set, filters to a single task — used on the cleaner's TaskDetailPage. */
  cleaningTaskId?: string
  /** When set, filters to one or more statuses — used on the owner-approver queue. */
  statuses?:       AdditionalFeeStatus[]
}

/** Pending-queue presets that the FeeRequestsPage uses by default. */
export const PENDING_QUEUE_STATUSES: readonly AdditionalFeeStatus[] = [
  'requested',
  'completed_without_approval',
] as const

export function useAdditionalFeeRequests(
  args: UseAdditionalFeeRequestsArgs = {},
): UseQueryResult<AdditionalFeeRequestRow[]> {
  const supabase = useSupabase()
  const cleaningTaskId = args.cleaningTaskId
  const statuses       = args.statuses

  return useQuery({
    // Key includes BOTH filter values so each scope caches independently
    // — the cleaner's per-task view and the owner queue never collide.
    queryKey: ['additional-fee-requests', { cleaningTaskId: cleaningTaskId ?? null, statuses: statuses ?? null }],
    refetchInterval: cleaningTaskId ? false : 30_000,
    queryFn: async (): Promise<AdditionalFeeRequestRow[]> => {
      let query = supabase
        .from('additional_fee_requests')
        .select(
          'id, organization_id, cleaning_task_id, room_id, requested_by_user_id, status,' +
          ' requested_amount_cents, reason, photo_storage_paths,' +
          ' owner_decision_by_user_id, owner_decision_at, denial_reason,' +
          ' created_at, updated_at',
        )
        .order('created_at', { ascending: false })

      if (cleaningTaskId) {
        query = query.eq('cleaning_task_id', cleaningTaskId)
      }
      if (statuses && statuses.length > 0) {
        query = query.in('status', statuses as unknown as string[])
      }

      const { data, error } = await query
      if (error) throw error
      return (data ?? []) as unknown as AdditionalFeeRequestRow[]
    },
  })
}
